/**
 * BrandProvider — fetches the active brand from the backend and
 * exposes it (plus a refresh fn) to the React tree.
 *
 * Pushes the brand's primary/accent colors onto the document's
 * root CSS variables (--color-primary, --color-primary-dark,
 * --color-secondary), so Tailwind classes that read those vars
 * (e.g. `bg-primary`) repaint instantly on a brand switch. The
 * <link rel="icon"> is also updated when the brand carries a
 * favicon, so the browser tab favicon flips too.
 *
 * If the API call fails (offline / fresh DB / boot race), the
 * provider falls back to the same Century 21 Abrams values the
 * backend seed uses — so the public site never renders blank.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { apiClient } from '@/shared/api';
import type { Brand } from '@/types/brand';


/**
 * Generic placeholder used only while /brands/active is in-flight.
 * Intentionally brand-neutral — no brokerage names, no agent
 * identity, no Shayaan-specific values. The backend's
 * /api/brands/active falls back to .env-driven values so the
 * actual rendered brand always comes off the server.
 */
const FALLBACK_BRAND: Brand = {
    id: 0,
    name: 'Real Estate',
    short_name: null,
    slug: 'default',
    mission_statement: null,
    hero_title: 'Find Your Dream Home',
    hero_subtitle: null,
    logo_url: null,
    // Defaults intentionally match the Century 21 Abrams palette in
    // src/index.css (the seeded brand). If the `/brands/active`
    // endpoint returns null (fresh deploy, no row yet) or fails, we
    // want the live site to keep looking like itself rather than
    // flipping to a generic gray-blue. The Brand Manager overrides
    // these the moment the operator activates a different brokerage.
    primary_color: '#BEAF87',
    primary_dark_color: '#746649',
    accent_color: '#252526',
    theme_mode: 'light',
    is_active: false,
    is_default: false,
};


interface BrandContextValue {
    brand: Brand;
    loading: boolean;
    /** True while `brand` is the built-in placeholder rather than a configured brand. */
    isFallback: boolean;
    refresh: () => Promise<void>;
}

const BrandContext = createContext<BrandContextValue | null>(null);


/**
 * Apply this brand's colors to the document root so Tailwind's
 * `bg-primary` / `text-primary-dark` classes (which read CSS
 * variables defined in index.css) flip immediately.
 */
function applyThemeColors(brand: Brand): void {
    const root = document.documentElement;
    const set = (name: string, value?: string | null) => {
        if (value && /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(value)) {
            root.style.setProperty(name, value);
        }
    };
    set('--color-primary', brand.primary_color);
    set('--color-primary-dark', brand.primary_dark_color);
    set('--color-secondary', brand.accent_color);
    if (brand.text_color) set('--color-text', brand.text_color);
    applyThemeMode(brand.theme_mode === 'dark' ? 'dark' : 'light');
}


/** Browser chrome colour per scheme: charcoal either way, darker in dark mode. */
const THEME_META_COLOR: Record<'light' | 'dark', string> = { light: '#252526', dark: '#1A1A1B' };

/**
 * `<html data-theme="dark">` switches index.css's dark palette on (it
 * remaps Tailwind's colour variables, so no component needs `dark:`
 * classes); `color-scheme` makes native inputs and scrollbars match.
 */
function applyThemeMode(mode: 'light' | 'dark'): void {
    const root = document.documentElement;
    root.dataset.theme = mode;
    root.style.colorScheme = mode;
    document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')?.setAttribute('content', THEME_META_COLOR[mode]);
}


/** Swap the favicon when the brand carries one. */
function applyFavicon(brand: Brand): void {
    if (!brand.favicon_url) return;
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
    }
    link.href = brand.favicon_url;
}


export function BrandProvider({ children }: { children: ReactNode }) {
    const [brand, setBrand] = useState<Brand>(FALLBACK_BRAND);
    const [loading, setLoading] = useState(true);

    const fetchBrand = useCallback(async () => {
        try {
            const resp = await apiClient.get<{ success: boolean; data: Brand | null }>('/brands/active');
            const next = resp.data?.data;
            if (next) {
                setBrand(next);
                applyThemeColors(next);
                applyFavicon(next);
            } else {
                // Empty backend — keep the fallback so the page renders.
                applyThemeColors(FALLBACK_BRAND);
            }
        } catch {
            applyThemeColors(FALLBACK_BRAND);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void fetchBrand();
    }, [fetchBrand]);

    const value = useMemo<BrandContextValue>(
        () => ({ brand, loading, isFallback: brand === FALLBACK_BRAND, refresh: fetchBrand }),
        [brand, loading, fetchBrand],
    );

    return <BrandContext.Provider value={value}>{children}</BrandContext.Provider>;
}


/**
 * Read the active brand from anywhere in the tree. Throws when
 * called outside <BrandProvider/>; this is intentional — every
 * page is wrapped in App.tsx so a missing provider is a setup bug.
 */
export function useBrand(): BrandContextValue {
    const ctx = useContext(BrandContext);
    if (!ctx) {
        throw new Error('useBrand must be used within <BrandProvider/>');
    }
    return ctx;
}

/**
 * Like useBrand, but null outside <BrandProvider/>. For hooks that run
 * in isolated tests or on routes rendered without the app shell.
 */
export function useBrandOptional(): BrandContextValue | null {
    return useContext(BrandContext);
}
